'use client';

import React from 'react';
import { Heart, MessageCircle, Share2, Eye, TrendingUp } from 'lucide-react';


interface TopPost {
  id: string;
  title: string;
  type: string;
  platform: 'facebook' | 'instagram';
  publishedDate: string;
  reach: number;
  impressions: number;
  likes: number;
  comments: number;
  shares: number;
  engagementRate: number;
}

const topPostsData: Record<string, TopPost[]> = {
  'page-002': [
    { id: 'tp-001', title: 'Autumn Harvest Blend Reveal', type: 'Carousel', platform: 'instagram', publishedDate: 'Aug 4', reach: 38600, impressions: 82400, likes: 2140, comments: 156, shares: 0, engagementRate: 5.94 },
    { id: 'tp-002', title: 'Morning Ritual — 5 Ways to Brew', type: 'Reel', platform: 'instagram', publishedDate: 'Jul 29', reach: 31200, impressions: 68800, likes: 1820, comments: 134, shares: 0, engagementRate: 6.26 },
    { id: 'tp-003', title: 'Limited Edition: Pumpkin Spice', type: 'Image', platform: 'instagram', publishedDate: 'Jul 26', reach: 28400, impressions: 61200, likes: 1560, comments: 112, shares: 0, engagementRate: 5.89 },
    { id: 'tp-004', title: 'Behind the Roastery — Story', type: 'Story', platform: 'instagram', publishedDate: 'Aug 1', reach: 27800, impressions: 54200, likes: 1690, comments: 98, shares: 0, engagementRate: 6.43 },
    { id: 'tp-005', title: 'Barista Championship Feature', type: 'Video', platform: 'instagram', publishedDate: 'Jul 20', reach: 22100, impressions: 47600, likes: 1380, comments: 94, shares: 0, engagementRate: 6.68 },
    { id: 'tp-006', title: 'Coffee and Creativity Collab', type: 'Carousel', platform: 'instagram', publishedDate: 'Jul 17', reach: 19600, impressions: 42100, likes: 1240, comments: 88, shares: 0, engagementRate: 6.77 },
    { id: 'tp-007', title: 'Single Origin: Ethiopia Yirgacheffe', type: 'Image', platform: 'instagram', publishedDate: 'Jul 14', reach: 15800, impressions: 33400, likes: 1020, comments: 55, shares: 0, engagementRate: 6.80 },
    { id: 'tp-008', title: 'New Store Opening — Melbourne', type: 'Image', platform: 'instagram', publishedDate: 'Jul 11', reach: 18400, impressions: 39200, likes: 1140, comments: 78, shares: 0, engagementRate: 6.62 },
  ],
  default: [
    { id: 'tp-d01', title: 'Summer Campaign Hero', type: 'Image', platform: 'facebook', publishedDate: 'Aug 4', reach: 21400, impressions: 48200, likes: 560, comments: 71, shares: 96, engagementRate: 3.40 },
    { id: 'tp-d02', title: 'Product Demo Video', type: 'Video', platform: 'facebook', publishedDate: 'Jul 29', reach: 17400, impressions: 38600, likes: 480, comments: 58, shares: 82, engagementRate: 3.56 },
    { id: 'tp-d03', title: 'Customer Success Story', type: 'Carousel', platform: 'facebook', publishedDate: 'Jul 26', reach: 18200, impressions: 40100, likes: 510, comments: 62, shares: 88, engagementRate: 3.62 },
    { id: 'tp-d04', title: 'Tech Tips Tuesday #42', type: 'Image', platform: 'facebook', publishedDate: 'Aug 1', reach: 15900, impressions: 34800, likes: 440, comments: 52, shares: 74, engagementRate: 3.55 },
    { id: 'tp-d05', title: 'Q3 Product Announcement', type: 'Video', platform: 'facebook', publishedDate: 'Jul 20', reach: 14800, impressions: 31200, likes: 420, comments: 48, shares: 72, engagementRate: 3.65 },
    { id: 'tp-d06', title: 'Industry Report Infographic', type: 'Image', platform: 'facebook', publishedDate: 'Jul 17', reach: 12600, impressions: 26800, likes: 380, comments: 41, shares: 64, engagementRate: 3.85 },
    { id: 'tp-d07', title: 'Team Spotlight: Engineering', type: 'Image', platform: 'facebook', publishedDate: 'Jul 14', reach: 9100, impressions: 19400, likes: 280, comments: 22, shares: 38, engagementRate: 3.74 },
    { id: 'tp-d08', title: 'Partnership Announcement', type: 'Image', platform: 'facebook', publishedDate: 'Jul 11', reach: 10200, impressions: 21800, likes: 310, comments: 34, shares: 58, engagementRate: 3.94 },
  ],
};

interface Props {
  pageId: string;
  platform: 'facebook' | 'instagram';
}

export default function TopPostsTable({ pageId, platform }: Props) {
  const posts = topPostsData[pageId] || topPostsData.default;

  return (
    <div className="card-base overflow-hidden">
      <div className="px-6 py-4 border-b border-border">
        <h3 className="text-base font-semibold text-foreground">Top Posts by Engagement Rate</h3>
        <p className="text-xs text-muted-foreground mt-0.5">Last 30 days · Ranked by engagement rate</p>
      </div>
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/30">
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Post</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Type</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Date</th>
              <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <div className="flex items-center gap-1 justify-end"><Eye size={12} /> Reach</div>
              </th>
              <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <div className="flex items-center gap-1 justify-end"><Eye size={12} /> Impr.</div>
              </th>
              <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <div className="flex items-center gap-1 justify-end"><Heart size={12} /> Likes</div>
              </th>
              <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <div className="flex items-center gap-1 justify-end"><MessageCircle size={12} /> Cmts</div>
              </th>
              {platform === 'facebook' && (
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  <div className="flex items-center gap-1 justify-end"><Share2 size={12} /> Shares</div>
                </th>
              )}
              <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <div className="flex items-center gap-1 justify-end"><TrendingUp size={12} /> Eng. Rate</div>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {posts.map((post, idx) => (
              <tr key={post.id} className="table-row-hover">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-bold text-muted-foreground w-5 tabular-nums">{idx + 1}</span>
                    <p className="text-sm font-medium text-foreground max-w-[200px] truncate">{post.title}</p>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className="text-xs font-medium text-muted-foreground bg-muted px-2 py-0.5 rounded-full">{post.type}</span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-xs text-muted-foreground">{post.publishedDate}</span>
                </td>
                <td className="px-4 py-3 text-right">
                  <span className="text-sm tabular-nums text-foreground">{(post.reach / 1000).toFixed(1)}K</span>
                </td>
                <td className="px-4 py-3 text-right">
                  <span className="text-sm tabular-nums text-foreground">{(post.impressions / 1000).toFixed(1)}K</span>
                </td>
                <td className="px-4 py-3 text-right">
                  <span className="text-sm tabular-nums text-foreground">{post.likes.toLocaleString()}</span>
                </td>
                <td className="px-4 py-3 text-right">
                  <span className="text-sm tabular-nums text-foreground">{post.comments}</span>
                </td>
                {platform === 'facebook' && (
                  <td className="px-4 py-3 text-right">
                    <span className="text-sm tabular-nums text-foreground">{post.shares}</span>
                  </td>
                )}
                <td className="px-4 py-3 text-right">
                  <span className={`text-sm font-bold tabular-nums ${post.engagementRate >= 5.0 ? 'text-positive' : post.engagementRate >= 3.0 ? 'text-foreground' : 'text-warning'}`}>
                    {post.engagementRate.toFixed(2)}%
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}